// src/oura/newscan/fixtures/recipe.ts
//
// The recipe the fixture engine drafts from. The local templates (Local v4.5)
// are copied from the backend's v4.2 problem-led spec
// (ourteam-backend app/dependencies/aeo_visibility_spec.v4.2-problem-led.json:
// location_angles, city_slots, single_outlet_variant.extra_slots, the tier-1
// language templates, dead_shapes, category_cap). Local v4.5 is the v4.2
// problem-led local questions unchanged (spec: modes.local is v4.2 unchanged);
// see RECIPES for why the stamp still reads v4.2. The home-visit shape is the
// proposed one from the approved mock. Since v4.5 (15 Oct 2026) Online is
// brand mode: its 50 are pre-authored per sample from the Google suggestions
// pull (brand.ts, brandSamples.ts), never templated. Both (a showroom plus an
// online store) is plain Local v4.5: the showroom is an outlet and its
// products sit in the specialty and intent rows, with no market rows.
//
// Every template has a stable id, so a "try another" swap or a kept old
// wording can be stored by id and rendered again on every read.

import type { BuyMode, QuestionCheck, ScanQuestionCheck, WordFamily } from "@service/aeo/scanDraftTypes";

export interface TemplateDef {
    id: string;
    text: string;
    /** Local checks for the templates; a brand row's stand-in template carries its brand check. */
    check: ScanQuestionCheck;
    /** Recipe group. Location templates take `anchor` or `outlets` from the outlet they serve. */
    group: string;
}

const loc = (id: string, text: string): TemplateDef => ({ id, text, check: "location", group: "location" });

/* ------------------------------------------------------------------------- *
 * Local (Visit us), Local v4.5: the v4.2 problem-led questions, unchanged
 * ------------------------------------------------------------------------- */

/** location_angles 1 to 14, taken in order until an outlet's share is filled. */
export const LOCAL_ANGLES: TemplateDef[] = [
    loc("L1", "Where to fix {pain_point} in {area}?"),
    loc("L2", "Best {place_noun} in {area}?"),
    loc("L3", "Best place for {core_service} in {area}?"),
    loc("L4", "Where can I get {secondary_service_1} in {area}?"),
    loc("L5", "Is there a {place_noun} in {area_building}?"),
    loc("L6", "{core_service} near {area_transit}"),
    loc("L7", "Where to get {condition} treatment in {area}?"),
    loc("L8", "How much is {priced_service} in {area}?"),
    loc("L9", "{place_noun} in {area} open {situational}"),
    loc("L10", "Where can I get {secondary_service_2} in {area}?"),
    loc("L11", "Where to fix {pain_point_2} near {area_nearby_landmark}?"),
    loc("L12", "Where can I get {differentiator} in {area}?"),
    loc("L13", "Best {place_noun} in {area} for {audience_2}?"),
    loc("L14", "Where can I get {secondary_service_4} in {area}?"),
];

/** We go to them: the address angles become home-visit angles. */
export const HOME_SWAPS: Record<string, TemplateDef> = {
    L5: loc("H5", "Which {place_noun} covers {area}?"),
    L6: loc("H6", "{core_service} at home in {area}"),
    L9: loc("H9", "{place_noun} in {area} available {situational}"),
    L11: loc("H11", "Where to fix {pain_point_2} at home in {area}?"),
};

const slot = (n: number, check: QuestionCheck, group: string, text: string): TemplateDef => ({ id: `C${n}`, text, check, group });

/** city_slots 27 to 50: rendered in slot order with {city} rotated across the outlets' towns. */
export const CITY_SLOTS: TemplateDef[] = [
    slot(27, "specialty", "core_service", "Best place to fix {pain_point} in {city}?"),
    slot(28, "specialty", "core_service", "Where to get {core_service} in {city}?"),
    slot(29, "specialty", "core_service", "Best {condition} treatment in {city}?"),
    slot(30, "specialty", "core_service", "Which {place_noun} in {city} do people recommend most?"),
    slot(31, "specialty", "secondary_service", "Where to get {secondary_service_1} in {city}?"),
    slot(32, "specialty", "secondary_service", "Where to get {secondary_service_2} in {city}?"),
    slot(33, "specialty", "secondary_service", "Best place for {secondary_service_3} in {city}?"),
    slot(34, "specialty", "secondary_service", "Where can I get {secondary_service_4} in {city}?"),
    slot(35, "specialty", "differentiator", "Which {place_noun} in {city} has {differentiator}?"),
    slot(36, "specialty", "differentiator", "Where can I try {differentiator} in {city}?"),
    slot(37, "specialty", "audience", "Which {place_noun} in {city} is good for {audience_1}?"),
    slot(38, "specialty", "audience", "Which {place_noun} in {city} is good for {audience_2}?"),
    slot(39, "intent", "price", "How much does {priced_service} cost in {city}?"),
    slot(40, "intent", "price", "Where can I get {priced_service} for around {price_point} in {city}?"),
    slot(41, "intent", "price", "Where can I find affordable {core_service} in {city}?"),
    slot(42, "intent", "comparison", "Best {place_noun} in {city}?"),
    slot(43, "intent", "comparison", "Which {place_noun} in {city} should I go to for {condition}, {category} or {alternative_category}?"),
    slot(44, "intent", "comparison", "What are the top {place_noun}s in {city} right now?"),
    slot(45, "intent", "situational", "{place_noun} in {city} open {situational}"),
    slot(46, "intent", "booking", "Which {place_noun} in {city} takes same-day appointments?"),
    slot(47, "intent", "promotion", "Which {place_noun} in {city} has a {promo_phrase}?"),
    slot(48, "specialty", "audience", "Which {place_noun} in {city} is good for {common_concern}?"),
    slot(49, "intent", "comparison", "Which {place_noun} in {city} has the best reviews?"),
    slot(50, "specialty", "audience", "Where should I go for my first {core_service} in {city}?"),
];

/** We go to them: the two city slots that assume a visit. */
export const HOME_CITY_SWAPS: Record<string, TemplateDef> = {
    C45: { id: "HC45", text: "{place_noun} in {city} available {situational}", check: "intent", group: "situational" },
    C46: { id: "HC46", text: "Which {place_noun} in {city} can come the same day?", check: "intent", group: "booking" },
};

const extra = (i: number, check: QuestionCheck, group: string, text: string): TemplateDef => ({ id: `X${i}`, text, check, group });

/** single_outlet_variant.extra_slots: one outlet asks 14 + 36, these are the 12 extra city-level rows. */
export const SINGLE_EXTRAS: TemplateDef[] = [
    extra(1, "specialty", "core_service", "Where can I try {core_service} in {city}?"),
    extra(2, "specialty", "core_service", "Which {place_noun} in {city} is best for {condition}?"),
    extra(3, "specialty", "secondary_service", "Where can I get {secondary_service_5} in {city}?"),
    extra(4, "specialty", "secondary_service", "Where to get {secondary_service_6} in {city}?"),
    extra(5, "specialty", "differentiator", "Which {place_noun} in {city} is known for {differentiator}?"),
    extra(6, "specialty", "audience", "Which {place_noun} in {city} is good for {audience_3}?"),
    extra(7, "intent", "price", "Which {place_noun} in {city} publishes its prices openly?"),
    extra(8, "intent", "price", "Which {place_noun} in {city} is the best value for money for {core_service}?"),
    extra(9, "intent", "comparison", "What are the top rated {place_noun}s in {city}?"),
    extra(10, "intent", "comparison", "Which {place_noun} in {city} do people travel across town for?"),
    extra(11, "intent", "situational", "Which {place_noun} in {city} is open {situational_2}?"),
    extra(12, "intent", "booking", "Which {place_noun} in {city} takes walk-ins without an appointment?"),
];

/* ------------------------------------------------------------------------- *
 * Malay and Chinese, tier 1 (languages.malay / languages.chinese)
 * ------------------------------------------------------------------------- */

/** Keyed by the English template id each one replaces. */
export const MS_SWAPS: Record<string, TemplateDef> = {
    L1: loc("ms:L1", "Di mana nak rawat {pain_point_ms} di {area}?"),
    L8: loc("ms:L8", "Berapa harga {priced_service_ms} di {area}?"),
    C27: { id: "ms:C27", text: "Tempat terbaik untuk rawat {pain_point_ms} di {city}?", check: "specialty", group: "core_service" },
    C39: { id: "ms:C39", text: "Berapa kos {priced_service_ms} di {city}?", check: "intent", group: "price" },
};

export const ZH_SWAPS: Record<string, TemplateDef> = {
    L4: loc("zh:L4", "{area}哪里可以做{secondary_service_1_zh}？"),
    L7: loc("zh:L7", "{area}哪里可以治疗{condition_zh}？"),
    C28: { id: "zh:C28", text: "{city}哪里可以做{core_service_zh}？", check: "specialty", group: "core_service" },
    C44: { id: "zh:C44", text: "{city}最好的{place_noun_zh}有哪些？", check: "intent", group: "comparison" },
};

/** Language rows only go to the first two outlets (languages.outlets_translated). */
export const OUTLETS_TRANSLATED = 2;
/** languages.tier_1_from: Auto turns a language on from this share. */
export const LANGUAGE_TIER_1 = 0.3;

/* ------------------------------------------------------------------------- *
 * Lookups and shape rules
 * ------------------------------------------------------------------------- */

const ALL_TEMPLATES: TemplateDef[] = [
    ...LOCAL_ANGLES,
    ...Object.values(HOME_SWAPS),
    ...CITY_SLOTS,
    ...Object.values(HOME_CITY_SWAPS),
    ...SINGLE_EXTRAS,
    ...Object.values(MS_SWAPS),
    ...Object.values(ZH_SWAPS),
];

const BY_ID: Record<string, TemplateDef> = Object.fromEntries(ALL_TEMPLATES.map((t) => [t.id, t]));

export function templateById(id: string): TemplateDef | null {
    return BY_ID[id] ?? null;
}

/** The variables a template names, in order, once each. */
export function templateVars(text: string): string[] {
    const out: string[] = [];
    const re = /\{([a-z_0-9]+)\}/g;
    let m: RegExpExecArray | null = re.exec(text);
    while (m) {
        if (!out.includes(m[1])) out.push(m[1]);
        m = re.exec(text);
    }
    return out;
}

export const LOCATION_TOTAL = 26;
export const SINGLE_LOCATION_TOTAL = 14;
export const TOTAL = 50;
/** The slot whose group differs between v4.1 (booking) and v4.2 (promotion): it follows the new recipe. */
export const FORCED_SLOT_KEY = "C47";

/** outlet_shares: split as evenly as possible, flagship strictly larger (14/12, 10/8/8, 8/6/6/6, 6/5/5/5/5). */
export function outletShares(outlets: number, total: number): number[] {
    if (outlets <= 0) return [];
    if (outlets === 1) return [total];
    if (outlets >= total) return Array.from({ length: outlets }, (_, i) => (i < total ? 1 : 0));
    const base = Math.floor(total / outlets);
    const shares = Array.from({ length: outlets }, () => base);
    const rest = total - base * outlets;
    shares[0] += rest;
    if (rest === 0) {
        shares[0] += 1;
        shares[outlets - 1] -= 1;
    }
    return shares;
}

/** category_cap: the profession noun as the subject of more than this many questions. */
export const CATEGORY_CAP = { warnAbove: 12, failAbove: 25 } as const;

/** dead_shapes: question shapes proven never to name a business. */
export const DEAD_SHAPES: RegExp[] = [
    /^does .*\b(actually|really)\b/,
    /^what should i expect/,
    /^what happens (at|on|during) (my|a|the) first/,
    /\bor can i (do|just do|try) .* (myself|at home)/,
    /\bworth paying for\b/,
    /,? which is better\??$/,
    /^how (do|can|would) (i|you) (tell|check|know)/,
    /^how often should/,
    /^how much should .* cost/,
    /^(is|are) .* (worth it|worth the money) in malaysia\?$/,
    /^is it (better|safe|ok|okay) to\b/,
    /^what does .*\b(actually|really)\b/,
    /^what (does|do) .* do (for|to) (your|my|the|you)\b/,
    /^(can|will) .* (actually|really)\b/,
];

/** "good for" + one of these asks for the opposite of what the business fixes. */
export const NEGATIVE_AFTER_GOOD_FOR = /(pain after|intimidat|crowd|waiting|side effect|\bsags?\b|scarring)/;

/* ------------------------------------------------------------------------- *
 * Recipes by how customers buy
 * ------------------------------------------------------------------------- */

export interface RecipeInfo {
    /**
     * The backend's stable recipe id. For the local recipe it is
     * visibility-set-v4.2-problem-led, the backend's LOCAL_STAMP, and it does
     * not move with the label.
     */
    stamp: string;
    /**
     * Display only. "Local v4.5" is the v4.2 problem-led questions unchanged,
     * so it keeps the v4.2 stamp; only the label changed (#4317,
     * ourteam-backend#6066, #6056).
     */
    label: string;
}

/**
 * The local stamp is unchanged on purpose. Local v4.5 is the v4.2 problem-led
 * local questions unchanged (spec: modes.local is v4.2 unchanged); the stamp
 * visibility-set-v4.2-problem-led is the stable identifier and matches the
 * backend's LOCAL_STAMP (app/dependencies/aeo_query_spec.py, which
 * aeo_scan_draft.py RECIPE_LABELS maps to "Local v4.5"); only the label
 * changed (#4317, ourteam-backend#6066). There is no v4.5 local stamp.
 */
export const LOCAL_STAMP = "visibility-set-v4.2-problem-led";

export const RECIPES: Record<BuyMode, RecipeInfo> = {
    visit: { stamp: LOCAL_STAMP, label: "Local v4.5" },
    we_go_to_them: { stamp: "visibility-set-home-v1", label: "We go to them v1" },
    online: { stamp: "visibility-set-v4.5-brand", label: "Brand v4.5" },
    // Stamp = the backend's stable id (LOCAL_STAMP); "Local v4.5" is display only (#4317, ourteam-backend#6066, #6056).
    // A showroom is an outlet: Both uses Local v4.5, the same stable local stamp (modes.brand.decide_when, 15 Oct 2026).
    both: { stamp: LOCAL_STAMP, label: "Local v4.5" },
};

/** The profession-led recipe an existing client may still be on. */
export const OLD_LOCAL_RECIPE: RecipeInfo = { stamp: "visibility-set-v4.1", label: "Local v4.1" };

/* ------------------------------------------------------------------------- *
 * Words: plain labels and families
 * ------------------------------------------------------------------------- */

/** Variables derived from the Business step, never edited as words. */
export const PLACE_VARS = new Set(["area", "city", "anchor", "market", "area_building", "area_transit", "area_nearby_landmark"]);

const WORD_LABEL: Record<string, string> = {
    area: "Outlet area",
    city: "Town",
    anchor: "Flagship area",
    market: "Market",
    area_building: "Building at the outlet",
    area_transit: "Station near the outlet",
    area_nearby_landmark: "Landmark near the outlet",
    pain_point: "Problem customers type first",
    pain_point_2: "Second problem",
    condition: "Condition treated",
    common_concern: "What people worry about",
    core_service: "Main service",
    core_product: "Main product",
    product_noun: "What they sell, in one word",
    product_family: "Product family",
    secondary_service_1: "Other service (1)",
    secondary_service_2: "Other service (2)",
    secondary_service_3: "Other service (3)",
    secondary_service_4: "Other service (4)",
    secondary_service_5: "Other service (5)",
    secondary_service_6: "Other service (6)",
    differentiator: "What sets them apart",
    differentiator_2: "Feature people search for",
    warranty: "Warranty",
    audience_1: "Who it is for (1)",
    audience_2: "Who it is for (2)",
    audience_3: "Who it is for (3)",
    audience_4: "Who it is for (4)",
    audience_5: "Who it is for (5)",
    priced_service: "What people ask the price of",
    price_point: "Price",
    promo_phrase: "Promotion people search for",
    payment_option: "Way to pay",
    trial_offer: "Trial offer",
    return_reason: "Why people return it",
    delivery_promise: "Delivery promise",
    setup_phrase: "Setup",
    place_noun: "What customers call the place",
    store_noun: "What customers call the store",
    category: "Profession",
    alternative_category: "What else they consider",
    situational: "When they need it",
    situational_2: "When they need it (2)",
    quality: "Quality people compare",
    situation: "Where it goes",
    remote_region: "Hard-to-reach region",
    pain_point_ms: "Problem, in Malay",
    priced_service_ms: "Priced service, in Malay",
    secondary_service_1_zh: "Other service, in Chinese",
    condition_zh: "Condition, in Chinese",
    core_service_zh: "Main service, in Chinese",
    place_noun_zh: "Place, in Chinese",
};

const FAMILY_OF: Array<[WordFamily, string[]]> = [
    ["problem", ["pain_point", "pain_point_2", "condition", "common_concern"]],
    [
        "service",
        [
            "core_service",
            "core_product",
            "product_noun",
            "product_family",
            "secondary_service_1",
            "secondary_service_2",
            "secondary_service_3",
            "secondary_service_4",
            "secondary_service_5",
            "secondary_service_6",
            "differentiator",
            "differentiator_2",
            "warranty",
        ],
    ],
    ["audience", ["audience_1", "audience_2", "audience_3", "audience_4", "audience_5"]],
    ["price", ["priced_service", "price_point", "payment_option"]],
    ["offer", ["promo_phrase", "trial_offer", "return_reason", "delivery_promise", "setup_phrase"]],
    ["hours", ["situational", "situational_2"]],
    ["comparison", ["category", "alternative_category", "quality"]],
    ["place", ["place_noun", "store_noun", "situation", "remote_region", ...Array.from(PLACE_VARS)]],
];

export function wordFamily(variable: string): WordFamily {
    if (/_(ms|zh)$/.test(variable)) return "language";
    const hit = FAMILY_OF.find(([, vars]) => vars.includes(variable));
    return hit ? hit[0] : "other";
}

/** The other variables of a word's family (the dedupe pass's word fallback draws on them). */
export function familyMembers(variable: string): string[] {
    const hit = FAMILY_OF.find(([, vars]) => vars.includes(variable));
    return hit ? hit[1].filter((v) => v !== variable && !PLACE_VARS.has(v)) : [];
}

export function wordLabel(variable: string): string {
    return WORD_LABEL[variable] ?? variable.replace(/_/g, " ");
}

/** Where a staff service with no question goes in, last used first. */
export const SERVICE_SWAP_TARGETS = ["secondary_service_4", "secondary_service_6", "secondary_service_3"];
